import { useSWRConfig } from 'swr';

/**
 * The useGrispiMutation function is a custom hook in TypeScript React that triggers a mutation using
 * useSWRConfig based on a specified mutation key, revalidation flag, and data.
 * @param  - The `useGrispiMutation` function takes in the following parameters:
 * @returns The `useGrispiMutation` function is returning a call to the `mutate` function from
 * `useSWRConfig` with the following parameters:
 * 1. A callback function that checks if the key starts with the provided `mutationKey`.
 * 2. The `data` parameter which is of type `T`.
 * 3. An options object with the `revalidate` property set to the provided
 */
export default function useGrispiMutation<T>({
    mutationKey,
    data = undefined
}: {
    mutationKey: string | string[];
    data?: T;
}) {
    const { mutate } = useSWRConfig();
    const mutation = async () => {
        if (Array.isArray(mutationKey)) {
            mutationKey.forEach(
                async (mutationK) =>
                    await mutate(
                        (key) => {
                            return (
                                (typeof key === 'string' && key.startsWith(mutationK)) ||
                                (Array.isArray(key) && key[0].startsWith(mutationK))
                            );
                        },
                        data,
                        {
                            revalidate: true
                        }
                    )
            );
        } else {
            await mutate(
                (key) => {
                    return (
                        (typeof key === 'string' && key.startsWith(mutationKey)) ||
                        (Array.isArray(key) && key[0].startsWith(mutationKey))
                    );
                },
                data,
                {
                    revalidate: true
                }
            );
        }
    };

    return {
        mutate: mutation
    };
}
